/**
 * FallbackHeaderBar — header minimal affiché quand le Hub est indisponible.
 * Reproduit le layout 48px fixe (pas de décalage de contenu).
 * Affiche : KONITYS / [icone Radio] NomApp (couleur) | email + photo profil
 *
 * Props (même sous-ensemble que le vrai HeaderBar) :
 *   - currentAppName  (string)   Nom de l'app
 *   - currentAppColor (string)   Couleur hex de l'app
 *   - onLogout        (function)
 *   - user            (object)   { firstName, lastName, email, username, photoUrl }
 */

import { Radio } from 'lucide-react';

function getInitials(user) {
  if (!user) return '?';
  if (user.email) return user.email[0].toUpperCase();
  if (user.username) return user.username[0].toUpperCase();
  const first = (user.firstName || '')[0] || '';
  const last = (user.lastName || '')[0] || '';
  if (first || last) return `${first}${last}`.toUpperCase();
  return '?';
}

export default function FallbackHeaderBar({
  currentAppName = 'Application',
  currentAppColor = '#3B82F6',
  onLogout,
  user,
}) {
  const displayLabel = user?.email || user?.username || '';

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 40,
          height: 48,
          background: 'linear-gradient(to right, #ffffff, rgba(239,246,255,0.4))',
          borderBottom: '1px solid #D6DFED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* ─── Left: KONITYS / App icon + name (colored) ─── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1D2B', letterSpacing: '-0.02em' }}>KONITYS</span>
          <span style={{ color: '#D6DFED' }}>/</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 6,
              backgroundColor: `${currentAppColor}15`,
            }}
          >
            <Radio size={16} style={{ color: currentAppColor }} />
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, color: currentAppColor }}>
            {currentAppName}
          </span>
          <span
            style={{
              fontSize: 11,
              color: '#F59E0B',
              background: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: 6,
              padding: '2px 8px',
              fontWeight: 500,
              marginLeft: 4,
            }}
          >
            Hub temporairement indisponible
          </span>
        </div>

        {/* ─── Right: Photo + email + logout ─── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {user.photoUrl ? (
                <img
                  src={user.photoUrl}
                  alt=""
                  style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: currentAppColor,
                  color: '#fff',
                  display: user.photoUrl ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getInitials(user)}
              </div>
              {displayLabel && (
                <span style={{ fontSize: 13, fontWeight: 500, color: '#1A1D2B', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayLabel}
                </span>
              )}
            </div>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#DC2626',
                background: 'none',
                border: '1px solid #FECACA',
                borderRadius: 8,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              Déconnexion
            </button>
          )}
        </div>
      </div>
      <div style={{ height: 48 }} />
    </>
  );
}
